import { reactive, computed, watch, onMounted, onDeactivated, ref } from "vue";
import ArticleSpeechTimeSupporter from "~/lib/ArticleSpeechTimeSupporter";
import { StorableRef } from "~/lib/StorableRef";

// title: タイトルの読み上げをしている場合
// summary: 要約の読み上げをしている場合
// content: 本文の読み上げをしている場合
// cancel: 読み上げ停止ボタンを押した場合
type SpeechStateKey = "title" | "summary" | "content" | "cancel";

export interface Article {
  articleId: number;
  title: string;
  summary: string;
  content: string;
}

export const useSpeechState = (articleId: number, articles: Article[], emit) => { // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
  const speechState = reactive({
    paused: speechSynthesis.paused,
    speaking: speechSynthesis.speaking,
    currentArticleIndex: 0,
    currentSpeechKey: "title" as SpeechStateKey,
    readContentLength: 0,
    isSkipOnendEvent: false,
    supporter: new ArticleSpeechTimeSupporter(articleId)
  });

  const [rate, setRate] = StorableRef("article.speech.rate", 1);
  const [volume, setVolume] = StorableRef("article.speech.volume", 1);
  const isPaused = computed(() => speechState.paused);
  const isSpeaking = computed(() => speechState.speaking);
  const resetSpeechState = () => {
    speechState.currentSpeechKey = "title";
    speechState.readContentLength = 0;
    speechState.isSkipOnendEvent = false;
  };
  const updateSpeechState = () => {
    speechState.paused = speechSynthesis.paused;
    speechState.speaking = speechSynthesis.speaking;
  };

  const onboundaryEvent = (event: SpeechSynthesisEvent) => {
    speechState.supporter.countUp(event);
    // 途中読み上げできるように読んだ文字数を記録する
    speechState.readContentLength += event.charLength;
  };

  const onendEvent = () => {
    // 最後まで読み上げていない場合は何もしない
    // 一時停止でも発火するので制御する
    if (speechState.isSkipOnendEvent || (isSpeaking.value && isPaused.value)) {
      speechState.isSkipOnendEvent = false;
      return;
    }

    speechState.readContentLength = 0;
    switch (speechState.currentSpeechKey) {
    case "title":
      speechState.currentSpeechKey = "summary";
      speechSynthesis.speak(initSpeechSynthesisUtterance(articles[speechState.currentArticleIndex].summary));
      break;
    case "summary":
      speechState.currentSpeechKey = "content";
      speechSynthesis.speak(initSpeechSynthesisUtterance(articles[speechState.currentArticleIndex].content));
      break;
    default:
      if (speechState.currentArticleIndex < (articles.length -1) && speechState.currentSpeechKey !== "cancel") {
        speechState.currentArticleIndex += 1;
        playSpeech();
        break;
      }
      resetSpeechState();
      updateSpeechState();
      break;
    }
  };
  const initSpeechSynthesisUtterance = (text) => {
    const speechText = new SpeechSynthesisUtterance(text);
    // プロパティ設定
    speechText.rate = rate.value;
    // 完了時のイベント設定
    speechText.onboundary = onboundaryEvent;
    speechText.onend = onendEvent;
    return speechText;
  };
  const speechFromMiddle = (isSkipOnendEvent = true) => {
    if (speechState.currentSpeechKey === "cancel") { return; }

    speechState.isSkipOnendEvent = isSkipOnendEvent;
    speechSynthesis.cancel();
    const text = articles[speechState.currentArticleIndex][speechState.currentSpeechKey].slice(speechState.readContentLength);
    speechSynthesis.speak(initSpeechSynthesisUtterance(text));
  };

  const gaSent = ref(false);
  const sendGa = () => {
    if (process.env.NODE_ENV == "production" && !gaSent.value) {
      emit("ga");
      gaSent.value = true;
    }
  };

  const playSpeech = () => {
    // 一時停止 => 停止 => 再生の場合に再生できるように制御
    if (speechState.currentSpeechKey !== "cancel" && isPaused.value) {
      speechFromMiddle(false);
      return;
    }

    resetSpeechState();
    const speechTitle = initSpeechSynthesisUtterance(articles[speechState.currentArticleIndex].title);
    speechSynthesis.speak(speechTitle);
    sendGa();
  };

  const pauseSpeech = () => {
    // 一時停止でもonendのイベントが発火するので制御する
    speechSynthesis.pause();
  };

  // 読み上げを全て停止する
  const cancelSpeech = () => {
    speechState.currentSpeechKey = "cancel";
    speechState.currentArticleIndex = 0;
    speechState.readContentLength = 0;
    speechSynthesis.cancel();
  };

  const completedSpeech = () => {
    speechState.supporter.complete();
  };

  watch(rate, () => {
    setRate(rate.value);
    if (!isSpeaking.value || isPaused.value) { return; }
    speechFromMiddle();
  });

  watch(volume, () => {
    setVolume(volume.value);
    if (!isSpeaking.value || isPaused.value) { return; }
    speechFromMiddle();
  });

  onMounted(() => {
    // スピーチでキューイングされているものは全てリセットする
    speechSynthesis.cancel();
    // 読み上げ中だと中画面遷移先でも実行されたままになるので、画面遷移時に読み上げ停止する
    window.addEventListener("beforeunload", cancelSpeech);
    window.addEventListener("beforeunload", completedSpeech);
    // speechSynthesisと同期をとる
    setInterval(updateSpeechState, 200);
  });

  onDeactivated(() => {
    window.removeEventListener("beforeunload", cancelSpeech);
    window.removeEventListener("beforeunload", completedSpeech);
  });

  const isSpeechSupported = computed(() => {
    return "speechSynthesis" in window;
  });

  return {
    isSpeechSupported,
    isPaused,
    isSpeaking,
    rate,
    volume,
    speechState,
    playSpeech,
    pauseSpeech,
    cancelSpeech
  };
};
